import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  applyItemOverrides,
  type InfoItemDescriptor as InfoItemOverrideShape,
  type LayoutOverrides,
} from '@components/shared/configurable';
import { Text } from '../Text';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';

/**
 * Number of columns the label/value pairs are laid out in.
 * - 1: single column (default) — each pair stacked vertically.
 * - 2: two-column responsive grid — pairs flow left-to-right then wrap.
 * - `'auto'`: responsive auto-fill multi-column grid (two columns on narrow
 *   viewports, then `repeat(auto-fill, minmax(11rem, 1fr))` from `md` up) —
 *   reproduces the pre-migration `ClaimSummaryCard` info grid.
 */
export type InfoListColumns = 1 | 2 | 'auto';

/**
 * Visual layout variant for the label/value pairs.
 *
 * - `'stacked'` (default): the canonical semantic `<dl>` where each label
 *   (`<dt>`) sits **above** its value (`<dd>`) in its own column. Output is
 *   byte-identical to the original approved render — every existing consumer,
 *   showcase, and test relies on this being unchanged.
 * - `'inline'`: each pair renders as a single horizontal row — `icon` + a
 *   `label value` run on the **same line** (label muted, value adjacent),
 *   reproducing the pre-migration info-card look (e.g. `ClaimSummaryCard`'s
 *   grid of `Icon + "Date sinistre: 10/01/2024"` rows). Still a semantic
 *   `<dl>`/`<dt>`/`<dd>` — the `<dt>` and `<dd>` are laid out inline rather
 *   than stacked, so label↔value association is preserved.
 *
 * The variant is **presentation only**: both variants consume the same
 * `items` API and run the identical override pipeline (apply overrides → drop
 * `visible === false` → stable-sort by `order`), so rename / hide / reorder /
 * tooltip behave the same in either.
 */
export type InfoListVariant = 'stacked' | 'inline';

/**
 * Runtime descriptor for a single label/value pair rendered by {@link InfoList}.
 *
 * Extends the foundation's override-able surface (`key` / `label` / `order` /
 * `visible` / `tooltip` — the fields a tenant rule can mutate via
 * {@link LayoutOverrides.items}) with the render-only fields the component
 * needs but overrides never touch: `value`, `icon`, and `truncate`.
 *
 * The `key` is the stable contract a rule targets; `label` is display-only and
 * itself overridable. Labels and tooltips live in this config (i18n key or
 * literal) — never as inline JSX strings — so overrides have something to
 * target.
 */
export interface InfoItemDescriptor extends InfoItemOverrideShape {
  /** The value to display. Any renderable node (string, number, Badge, …). */
  value: React.ReactNode;
  /** Optional Lucide icon rendered before the value. */
  icon?: LucideIcon;
  /**
   * Truncate the value to a single line with an ellipsis.
   * @default false
   */
  truncate?: boolean;
  /**
   * `'inline'` variant only: render this pair as a two-line **stacked** cell —
   * the muted label sits on its own line *above* the value (instead of the
   * default single-line `label value` run) — matching the original
   * `ClaimSummaryCard` manager rows (`Gestionnaire mat:` / `Gestionnaire
   * corp:`). Render-only and never touched by overrides.
   * @default false
   */
  block?: boolean;
}

/**
 * Base props for the {@link InfoList} component.
 */
interface InfoListBaseProps {
  /**
   * The label/value pairs to display. Rendered after applying
   * {@link layoutOverrides}, filtering out `visible === false` items, and
   * sorting by `order` (ascending; items without an `order` keep their
   * declaration order and sort after ordered items).
   */
  items: ReadonlyArray<InfoItemDescriptor>;
  /**
   * Number of columns to lay the pairs out in.
   *
   * Honoured by both variants. In `'inline'` the same responsive grid is used,
   * so `columns={2}` flows the inline rows into a two-column grid, and
   * `columns="auto"` uses the responsive auto-fill multi-column grid that
   * reproduces the pre-migration `ClaimSummaryCard` info grid (two columns on
   * narrow viewports, then `repeat(auto-fill, minmax(11rem, 1fr))` from `md`).
   * @default 1
   */
  columns?: InfoListColumns;
  /**
   * Visual layout variant. `'stacked'` (default) keeps the original
   * `<dt>`-over-`<dd>` look; `'inline'` renders each pair as a single
   * `label value` row. See {@link InfoListVariant}.
   * @default 'stacked'
   */
  variant?: InfoListVariant;
  /**
   * `'inline'` variant only: when set, the per-item icon is rendered as the
   * **raw Lucide icon** carrying this className (so it keeps Lucide's default
   * `strokeWidth={2}`) instead of the `<Icon size="sm">` wrapper (which forces
   * the thinner `strokeWidth={1.5}`). Use it for exact pre-migration icon
   * parity — e.g. `ClaimSummaryCard` passes
   * `"w-4 h-4 text-primary-400 shrink-0"`. When omitted, icons keep the default
   * `<Icon size="sm">` rendering. No effect in the `'stacked'` variant.
   */
  iconClassName?: string;
  /**
   * Tenant-scoped layout overrides. The `items` bucket is consumed directly;
   * when it is absent the `fields` bucket is used as a fallback, because the
   * rule engine folds info-card item edits into `fields` (info items are
   * authored as `kind: 'field'` targets). The chosen bucket is merged onto
   * {@link items} (by `key`) before render via `applyItemOverrides`. With no
   * rules authored this is `undefined`/empty and the output is byte-identical
   * to passing the raw `items`.
   */
  layoutOverrides?: LayoutOverrides;
  /**
   * Additional CSS classes for the root element.
   */
  className?: string;
}

/**
 * InfoListProps extends HTML div attributes.
 */
export type InfoListProps = InfoListBaseProps &
  Omit<React.HTMLAttributes<HTMLDListElement>, keyof InfoListBaseProps>;

/**
 * Sort comparator for descriptors: ascending by `order`, with descriptors that
 * have no `order` sorting *after* all ordered ones while preserving their
 * relative declaration order (stable sort). Matches how the auto-render hosts
 * (`<EntityForm.Fields/>`, DataList) treat a missing `order`.
 */
function compareOrder(a: InfoItemDescriptor, b: InfoItemDescriptor): number {
  const ao = a.order ?? Number.POSITIVE_INFINITY;
  const bo = b.order ?? Number.POSITIVE_INFINITY;
  return ao - bo;
}

/**
 * Column layout classes. A single column stacks pairs vertically; two columns
 * use a responsive grid that collapses to one column on narrow viewports.
 */
const columnClasses: Record<InfoListColumns, string> = {
  1: 'grid grid-cols-1 gap-x-6 gap-y-4',
  2: 'grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4',
  auto:
    'grid grid-cols-2 md:grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-x-6 gap-y-4',
};

/**
 * InfoList — the canonical, declarative render host for read-only "info" cards
 * (label/value pairs). Renders an `items` array as a semantic description list
 * (`<dl>` / `<dt>` / `<dd>`), composing the base {@link Text}, {@link Icon},
 * and {@link Tooltip} components.
 *
 * Presentational only: no data fetching and no rule-engine coupling beyond
 * consuming a passed {@link LayoutOverrides}. It is **not** itself registered
 * as a configurable component — the consuming cards (e.g. `ClaimSummaryCard`,
 * `CloseTaskCard`) are, and they thread their `layoutOverrides` through here.
 *
 * Render pipeline: apply `layoutOverrides.items` (falling back to
 * `layoutOverrides.fields`, which is the bucket the rule engine fills for info
 * items) → drop `visible === false` → stable-sort by `order` (ascending;
 * missing `order` sorts last) → render each as a label/value pair.
 *
 * Two presentation variants (see {@link InfoListVariant}) share that one
 * pipeline: `'stacked'` (default) renders `<dt>` over `<dd>`; `'inline'`
 * renders each pair as a single `icon + "label value"` row (the pre-migration
 * info-card look). The variant changes only layout — overrides behave
 * identically in both.
 *
 * @example
 * // Basic single-column info card
 * <InfoList
 *   items={[
 *     { key: 'claimNumber', label: 'Claim number', value: 'CLM-2024-001' },
 *     { key: 'status', label: 'Status', value: <Badge>Open</Badge> },
 *   ]}
 * />
 *
 * @example
 * // Two columns, with icon, tooltip, truncation, and a tenant override
 * <InfoList
 *   columns={2}
 *   items={[
 *     { key: 'date', label: 'Date', value: '2024-01-15', icon: Calendar },
 *     { key: 'ref', label: 'Reference', value: longRef, truncate: true,
 *       tooltip: 'Internal reference number' },
 *   ]}
 *   layoutOverrides={{ items: { ref: { label: 'Réf.', visible: false } } }}
 * />
 */
const InfoList = React.forwardRef<HTMLDListElement, InfoListProps>(
  (
    {
      items,
      columns = 1,
      variant = 'stacked',
      iconClassName,
      layoutOverrides,
      className = '',
      ...props
    },
    ref,
  ) => {
    // 1. Apply tenant overrides (label/visible/order/tooltip by key) FIRST.
    // 2. Filter out items explicitly hidden by `visible === false`.
    // 3. Stable-sort by `order` (ascending; missing order sorts last).
    //
    // This pipeline is variant-independent: both `'stacked'` and `'inline'`
    // consume the identical resolved-and-ordered descriptor list, so overrides
    // (rename / hide / reorder / tooltip) behave the same in either.
    const visibleItems = React.useMemo(() => {
      // The rule engine folds info-card item edits (rename / hide / reorder /
      // tooltip) into the `fields` bucket: info items are surfaced to the
      // authoring panel as `kind: 'field'` targets and there is no item-kind
      // effect, so `finaliseLayoutOverrides` only ever emits `fields`, never
      // `items`. Read the documented `items` bucket first, then fall back to
      // `fields` so engine-produced overrides actually apply here (without the
      // fallback, ClaimSummaryCard / CloseTaskCard ignore every override).
      // Both buckets share the `Record<string, FieldOverride>` shape.
      const itemOverrides = layoutOverrides?.items ?? layoutOverrides?.fields;
      const overridden = applyItemOverrides(items, itemOverrides);
      return overridden
        .filter((item) => item.visible !== false)
        .slice()
        .sort(compareOrder);
    }, [items, layoutOverrides?.items, layoutOverrides?.fields]);

    const combinedClasses = [columnClasses[columns], className]
      .filter(Boolean)
      .join(' ');

    if (variant === 'inline') {
      return (
        <dl ref={ref} className={combinedClasses} {...props}>
          {visibleItems.map((item) => (
            <InlinePair
              key={item.key}
              item={item}
              iconClassName={iconClassName}
            />
          ))}
        </dl>
      );
    }

    return (
      <dl ref={ref} className={combinedClasses} {...props}>
        {visibleItems.map((item) => {
          const value = (
            <Text
              as="span"
              size="sm"
              weight="medium"
              truncate={item.truncate ?? false}
              className="text-secondary-900 min-w-0"
            >
              {item.value}
            </Text>
          );

          return (
            <div key={item.key} className="flex flex-col gap-0.5 min-w-0">
              {item.label !== undefined && (
                <dt className="m-0">
                  <Text
                    as="span"
                    size="xs"
                    weight="medium"
                    color="muted"
                    className="uppercase tracking-[0.04em]"
                  >
                    {item.label}
                  </Text>
                </dt>
              )}
              <dd className="m-0 flex items-center gap-1.5 min-w-0">
                {item.icon && (
                  <Icon
                    icon={item.icon}
                    size="sm"
                    color="muted"
                    className="flex-shrink-0"
                  />
                )}
                {item.tooltip !== undefined ? (
                  <Tooltip content={item.tooltip}>{value}</Tooltip>
                ) : (
                  value
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    );
  },
);

/**
 * `'inline'`-variant row. Two shapes, selected per-item by `item.block`:
 *
 * - **single-line (default)**: `icon` + a single-line `label value` run.
 *   Markup mirrors the pre-migration `ClaimSummaryCard` `InfoItem`: a flex row
 *   (`flex items-center gap-1.5 min-w-0`) holding the per-item icon and, on the
 *   **same line**, the muted `<dt>` label (with a trailing space) immediately
 *   followed by the `<dd>` value. The flex `<div>` carries `truncate` so a long
 *   single-line row clips with an ellipsis instead of wrapping; when an item is
 *   `truncate` or has an explicit `tooltip`, the value is wrapped in a
 *   `Tooltip` so the full text stays reachable on hover.
 * - **two-line stacked (`item.block`)**: `icon` + a `min-w-0` column with the
 *   muted `<dt>` label on its own line *above* the `<dd>` value (which truncates
 *   on its own line). Reproduces the original two-line manager rows
 *   (`Gestionnaire mat:` / `Gestionnaire corp:`): outer `flex items-start
 *   gap-1.5 min-w-0`, icon nudged with `mt-0.5`, value `block truncate` with
 *   **no** auto-Tooltip on truncation (the original didn't add one) — only an
 *   explicit `item.tooltip` wraps the value.
 *
 * The per-item icon renders one of two ways: when `iconClassName` is provided
 * it is the **raw Lucide icon** with that className (Lucide default
 * `strokeWidth={2}` — the pre-migration accent), otherwise the
 * `<Icon size="sm">` wrapper (`strokeWidth={1.5}`). For `block` rows `mt-0.5`
 * is appended to the icon className either way so it aligns with the label line.
 *
 * A11y: the row stays a `<dt>`/`<dd>` pair inside the parent `<dl>` in both
 * shapes (only their CSS flow differs — inline vs. stacked), so the label↔value
 * association is preserved.
 */
function InlinePair({
  item,
  iconClassName,
}: {
  item: InfoItemDescriptor;
  iconClassName?: string;
}) {
  const truncate = item.truncate ?? false;
  const block = item.block ?? false;

  const icon = item.icon
    ? iconClassName !== undefined
      ? // Raw Lucide icon (default strokeWidth={2}) — pre-migration parity.
        <item.icon
          className={[iconClassName, block ? 'mt-0.5' : '']
            .filter(Boolean)
            .join(' ')}
        />
      : <Icon
          icon={item.icon}
          size="sm"
          className={['text-primary-400 shrink-0', block ? 'mt-0.5' : '']
            .filter(Boolean)
            .join(' ')}
        />
    : null;

  if (block) {
    // Two-line stacked cell: muted label above a truncating value, matching the
    // original `ClaimSummaryCard` manager rows. No auto-Tooltip on truncation
    // (the original had none); an explicit `item.tooltip` still wraps the value.
    const blockValue = (
      <Text as="span" size="sm" weight="medium" className="block truncate">
        {item.value}
      </Text>
    );

    return (
      <div className="flex items-start gap-1.5 min-w-0">
        {icon}
        <div className="min-w-0">
          {item.label !== undefined && (
            <dt className="m-0 block">
              <Text as="span" size="xs" color="muted" className="block">
                {item.label}
              </Text>
            </dt>
          )}
          <dd className="m-0 block truncate">
            {item.tooltip !== undefined ? (
              <Tooltip content={item.tooltip}>{blockValue}</Tooltip>
            ) : (
              blockValue
            )}
          </dd>
        </div>
      </div>
    );
  }

  const value = (
    <Text as="span" size="sm" weight="medium">
      {item.value}
    </Text>
  );

  // The trailing space on the label reproduces the original
  // "Date sinistre: 10/01/2024" spacing. `<dt>`/`<dd>` render inline so the
  // label and value share one line.
  return (
    <div
      className={[
        'flex items-center gap-1.5 min-w-0',
        truncate ? 'truncate' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {icon}
      {item.label !== undefined && (
        <dt className="m-0 inline">
          <Text as="span" size="xs" color="muted">
            {item.label}{' '}
          </Text>
        </dt>
      )}
      <dd className="m-0 inline min-w-0">
        {item.tooltip !== undefined ? (
          <Tooltip content={item.tooltip}>{value}</Tooltip>
        ) : truncate ? (
          <Tooltip content={toTooltipText(item.value)}>{value}</Tooltip>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

/**
 * Best-effort tooltip text for a truncated value that has no explicit
 * `tooltip`. Strings/numbers map to themselves; richer nodes fall back to an
 * empty string (the Tooltip then has no extra content but the truncation
 * affordance is still wired, matching the original behaviour where the raw
 * string value was passed through).
 */
function toTooltipText(value: React.ReactNode): string {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
}

InfoList.displayName = 'InfoList';

export { InfoList };
